import React, { useState, useMemo, useCallback } from 'react';
import { Box, Typography, Stack } from 'omaha-design-system';
import type {
  NbaRatesStatsWithBaselineResponse,
  NbaRatesPlayerStats,
  ColumnDef,
  SortConfig,
  CategoryTab,
  RateMode,
  FantasyPlatform,
} from './types';
import {
  calcDkFpts,
  calcFptsPerMin,
  calcFdFpts,
  calcFdFptsPerMin,
  getCellValue,
  getDeltaBackground,
  calcRelativeDelta,
  calcPer100,
  calcPer36,
  calcPer1,
  getPossessions,
  RELATIVE_THRESHOLD,
  buildCustomColumns,
  loadCustomColumnKeys,
  saveCustomColumnKeys,
  COLUMNS_BY_CATEGORY,
  CATEGORY_TAB_LABELS,
  CATEGORY_TAB_ORDER,
  AGG_SUMMARY_BY_CATEGORY,
  formatDec2,
} from './statsCalculations';
import NbaRatesPlayerDetailCard from './NbaRatesPlayerDetailCard';
import NbaRatesCustomColumnPicker from './NbaRatesCustomColumnPicker';
import { NBA_TEAMS } from './types';
import { getTeamInfo } from '../stokasticProps/data/nbaTeams';

interface NbaRatesStatsTableProps {
  data: NbaRatesStatsWithBaselineResponse;
  teamName: string;
  teamId: string;
  filterTerms: string[];
}

const getTeamLogoUrl = (teamId: string): string | undefined => {
  const team = NBA_TEAMS.find((t) => t.id === teamId);
  if (!team) return undefined;
  return getTeamInfo(team.abbreviation).logo || undefined;
};

const RATE_MODE_LABELS: Record<RateMode, string> = {
  totals: 'Totals',
  per100: 'Per 100',
  per36: 'Per 36',
  per1: 'Per 1',
};

// The visible buttons - per36 and per1 share a slot and toggle between each other
const RATE_MODE_BUTTONS: RateMode[] = ['totals', 'per100', 'per36'];

const NbaRatesStatsTable: React.FC<NbaRatesStatsTableProps> = ({
  data,
  teamName,
  teamId,
  filterTerms,
}) => {
  const [categoryTab, setCategoryTab] = useState<CategoryTab>('stokastic');
  const [rateMode, setRateMode] = useState<RateMode>('totals');
  const [fantasyPlatform, setFantasyPlatform] = useState<FantasyPlatform>('dk');
  const [sort, setSort] = useState<SortConfig>({ key: 'minutes', direction: 'desc' });
  const [selectedPlayerId, setSelectedPlayerId] = useState<string | null>(null);
  const [customColumnKeys, setCustomColumnKeys] = useState<string[]>(loadCustomColumnKeys);
  const [showColumnPicker, setShowColumnPicker] = useState(false);
  const [copyStatus, setCopyStatus] = useState<'idle' | 'copied'>('idle');

  const handleCustomColumnsChange = useCallback((keys: string[]) => {
    setCustomColumnKeys(keys);
    saveCustomColumnKeys(keys);
  }, []);

  const customColumns = useMemo(() => buildCustomColumns(customColumnKeys), [customColumnKeys]);

  const { filtered, baseline } = data;

  // ── Column Resolution ─────────────────────────────────────────────────
  const currentColumns = useMemo(() => {
    if (categoryTab === 'custom') return customColumns;
    let cols = COLUMNS_BY_CATEGORY[categoryTab];
    // On stokastic tab, swap FPTS column based on fantasy platform
    if (categoryTab === 'stokastic') {
      cols = cols.map((col) => {
        if (col.key === 'dk_fpts' && fantasyPlatform === 'fd') {
          return { ...col, key: 'fd_fpts', label: 'FPTS' };
        }
        if (col.key === 'fpts_per_min' && fantasyPlatform === 'fd') {
          return { ...col, key: 'fd_fpts_per_min', label: 'FP/M' };
        }
        return col;
      });
    }
    return cols;
  }, [categoryTab, customColumns, fantasyPlatform]);

  // Build baseline lookup by player_id
  const baselineMap = useMemo(() => {
    const map = new Map<string, NbaRatesPlayerStats>();
    if (baseline?.players) {
      baseline.players.forEach((p) => map.set(p.player_id, p));
    }
    return map;
  }, [baseline]);

  const hasBaseline = baselineMap.size > 0;

  // Sorted players
  const sortedPlayers = useMemo(() => {
    return [...filtered.players].sort((a, b) => {
      const col = currentColumns.find((c) => c.key === sort.key);

      let aVal: number | string;
      let bVal: number | string;

      if (sort.key === 'player_name') {
        aVal = a.player_name ?? '';
        bVal = b.player_name ?? '';
      } else if (col) {
        aVal = getCellValue(a, sort.key, col, rateMode) ?? 0;
        bVal = getCellValue(b, sort.key, col, rateMode) ?? 0;
      } else {
        aVal = (a[sort.key as keyof NbaRatesPlayerStats] as number) ?? 0;
        bVal = (b[sort.key as keyof NbaRatesPlayerStats] as number) ?? 0;
      }

      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return sort.direction === 'asc'
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }

      const numA = typeof aVal === 'number' ? aVal : 0;
      const numB = typeof bVal === 'number' ? bVal : 0;
      return sort.direction === 'asc' ? numA - numB : numB - numA;
    });
  }, [filtered.players, sort, rateMode, currentColumns]);

  const handleSort = (key: string) => {
    if (sort.key === key) {
      setSort({ key, direction: sort.direction === 'asc' ? 'desc' : 'asc' });
    } else {
      setSort({ key, direction: 'desc' });
    }
  };

  const handleCategoryChange = (tab: CategoryTab) => {
    setCategoryTab(tab);
    // Reset sort to first meaningful column of the new tab
    // Default sort by minutes (most playing time at top)
    setSort({ key: 'minutes', direction: 'desc' });
  };

  const handleCopyToClipboard = useCallback(() => {
    if (currentColumns.length === 0) return;

    // Header row
    const headers = ['Player', ...currentColumns.map((c) => c.label + (c.isPct ? '%' : ''))];

    // Data rows — use the same sorted order the user sees
    const rows = sortedPlayers.map((player) => {
      const name = player.player_name || '-';
      const values = currentColumns.map((col) => {
        const val = getCellValue(player, col.key, col, rateMode);
        // Use formatDec2 for per1 mode (extra precision for small values)
        if (rateMode === 'per1' && col.isCountingStat) {
          return formatDec2(val);
        }
        // Use rate format when in per100/per36 and it's a counting stat
        if (rateMode !== 'totals' && col.isCountingStat && col.formatRate) {
          return col.formatRate(val);
        }
        return col.format(val);
      });
      return [name, ...values];
    });

    const tsv = [headers.join('\t'), ...rows.map((r) => r.join('\t'))].join('\n');

    navigator.clipboard.writeText(tsv).then(() => {
      setCopyStatus('copied');
      setTimeout(() => setCopyStatus('idle'), 2000);
    }).catch(() => {
      // Fallback: silent fail
    });
  }, [currentColumns, rateMode, sortedPlayers]);

  if (filtered.players.length === 0) {
    return null;
  }

  const agg = filtered.aggregate;
  const summaryItems = AGG_SUMMARY_BY_CATEGORY[categoryTab];

  // Footer text
  const getFooterLeft = (): string => {
    if (categoryTab === 'custom') {
      return `Custom view \u2022 ${customColumnKeys.length} columns selected`;
    }
    if (categoryTab === 'stokastic' && rateMode === 'totals') {
      return fantasyPlatform === 'dk'
        ? 'Fantasy: DraftKings (PTS + REB\u00D71.25 + AST\u00D71.5 + STL\u00D72 + BLK\u00D72 - TOV\u00D70.5 + 3PM\u00D70.5)'
        : 'Fantasy: FanDuel (PTS + REB\u00D71.2 + AST\u00D71.5 + STL\u00D73 + BLK\u00D73 - TOV)';
    }
    if (hasBaseline && rateMode !== 'totals') {
      return 'Cell shading: green = above season avg, red = below (hover for details)';
    }
    return '';
  };

  const getFooterRight = (): string => {
    if (rateMode === 'per100') return 'Stats per 100 possessions';
    if (rateMode === 'per36') return 'Stats per 36 minutes';
    if (rateMode === 'per1') return 'Stats per minute';
    if (categoryTab === 'custom') return 'Custom statistics';
    return 'Total statistics';
  };

  return (
    <Box
      sx={{
        bgcolor: '#0D0F13',
        borderRadius: '10px',
        border: '1px solid #1E2330',
        overflow: 'hidden',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.3)',
      }}
    >
      {/* Row 1: Team + Contextual Summary + Controls */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: '0.75rem 1.25rem',
          background: 'linear-gradient(135deg, #1A1D24 0%, #14171C 100%)',
          borderBottom: '1px solid #1E2330',
          flexWrap: 'wrap',
          gap: '0.75rem',
        }}
      >
        {/* Left: Team + Contextual Aggregate Stats */}
        <Stack direction="row" alignItems="center" sx={{ flexWrap: 'wrap', gap: '1rem' }}>
          {/* Team Logo + Name */}
          <Stack direction="row" alignItems="center" spacing={0.75}>
            {getTeamLogoUrl(teamId) && (
              <Box
                component="img"
                src={getTeamLogoUrl(teamId)}
                alt={teamName}
                sx={{ width: 24, height: 24, objectFit: 'contain' }}
              />
            )}
            <Typography
              sx={{
                fontSize: '0.95rem',
                fontWeight: 700,
                color: '#E8EAED',
                letterSpacing: '0.02em',
              }}
            >
              {teamName}
            </Typography>
          </Stack>

          <Divider />

          {/* Contextual Summary Stats */}
          {summaryItems.map((item, idx) => {
            const isLast = idx === summaryItems.length - 1;
            const needsDivider = idx === 1 && summaryItems.length > 2; // After POSS
            return (
              <React.Fragment key={item.key}>
                {needsDivider && <Divider />}
                <AggStat
                  label={item.label}
                  value={item.format(agg)}
                  valueColor={item.valueColor ? item.valueColor(agg) : undefined}
                  bold={item.bold}
                />
                {!isLast && idx === summaryItems.length - 3 && summaryItems.length > 4 && <Divider />}
              </React.Fragment>
            );
          })}
        </Stack>

        {/* Right: DK/FD Toggle + Rate Mode Pill + Copy */}
        <Stack direction="row" alignItems="center" spacing={0.75}>
          {/* DK/FD Toggle — only on stokastic tab */}
          {categoryTab === 'stokastic' && (
            <Box
              sx={{
                display: 'flex',
                bgcolor: '#0D0F13',
                borderRadius: '6px',
                p: '2px',
                border: '1px solid #1E2330',
              }}
            >
              {(['dk', 'fd'] as FantasyPlatform[]).map((platform) => (
                <Box
                  key={platform}
                  component="button"
                  onClick={() => {
                    setFantasyPlatform(platform);
                    // If currently sorted by fpts, update sort key
                    if (sort.key === 'dk_fpts' || sort.key === 'fd_fpts') {
                      setSort((s) => ({
                        ...s,
                        key: platform === 'dk' ? 'dk_fpts' : 'fd_fpts',
                      }));
                    }
                    if (sort.key === 'fpts_per_min' || sort.key === 'fd_fpts_per_min') {
                      setSort((s) => ({
                        ...s,
                        key: platform === 'dk' ? 'fpts_per_min' : 'fd_fpts_per_min',
                      }));
                    }
                  }}
                  sx={{
                    px: 1.25,
                    py: 0.375,
                    fontSize: '0.75rem',
                    fontWeight: 700,
                    background:
                      fantasyPlatform === platform
                        ? 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)'
                        : 'transparent',
                    border: 'none',
                    borderRadius: '4px',
                    color: fantasyPlatform === platform ? 'white' : '#6B7280',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                  }}
                >
                  {platform.toUpperCase()}
                </Box>
              ))}
            </Box>
          )}

          {/* Rate Mode Pill */}
          <Box
            sx={{
              display: 'flex',
              bgcolor: '#0D0F13',
              borderRadius: '8px',
              p: '3px',
              border: '1px solid #1E2330',
            }}
          >
            {RATE_MODE_BUTTONS.map((mode) => {
              // Determine if this button is "active"
              // For per36 slot: active when rateMode is per36 OR per1
              const isActive = mode === 'per36'
                ? (rateMode === 'per36' || rateMode === 'per1')
                : rateMode === mode;

              // Determine the display label
              // For per36 slot: show "Per 1" if that's the current mode
              const displayLabel = mode === 'per36' && rateMode === 'per1'
                ? RATE_MODE_LABELS.per1
                : RATE_MODE_LABELS[mode];

              // Click handler with toggle logic
              const handleClick = () => {
                if (mode === 'per36') {
                  // Toggle between per36 and per1
                  if (rateMode === 'per36') {
                    setRateMode('per1');
                  } else if (rateMode === 'per1') {
                    setRateMode('per36');
                  } else {
                    setRateMode('per36');
                  }
                } else {
                  setRateMode(mode);
                }
              };

              return (
                <Box
                  key={mode}
                  component="button"
                  onClick={handleClick}
                  sx={{
                    px: 1.5,
                    py: 0.5,
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    background: isActive
                      ? 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)'
                      : 'transparent',
                    border: 'none',
                    borderRadius: '6px',
                    color: isActive ? 'white' : '#6B7280',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                  }}
                >
                  {displayLabel}
                </Box>
              );
            })}
          </Box>

          {/* Copy to clipboard */}
          <Box
            component="button"
            onClick={handleCopyToClipboard}
            title="Copy table to clipboard"
            sx={{
              width: 32,
              height: 32,
              borderRadius: '8px',
              border: '1px solid',
              borderColor: copyStatus === 'copied' ? '#22c55e' : '#1E2330',
              bgcolor: copyStatus === 'copied' ? 'rgba(34,197,94,0.1)' : '#0D0F13',
              color: copyStatus === 'copied' ? '#22c55e' : '#6B7280',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.15s ease',
              '&:hover': {
                color: copyStatus === 'copied' ? '#22c55e' : '#3B82F6',
                borderColor: copyStatus === 'copied' ? '#22c55e' : '#3B82F6',
              },
            }}
          >
            {copyStatus === 'copied' ? (
              <Box
                component="svg"
                viewBox="0 0 20 20"
                fill="currentColor"
                sx={{ width: 16, height: 16 }}
              >
                <path
                  fillRule="evenodd"
                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                  clipRule="evenodd"
                />
              </Box>
            ) : (
              <Box
                component="svg"
                viewBox="0 0 20 20"
                fill="currentColor"
                sx={{ width: 16, height: 16 }}
              >
                <path d="M8 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z" />
                <path d="M6 3a2 2 0 00-2 2v11a2 2 0 002 2h8a2 2 0 002-2V5a2 2 0 00-2-2 3 3 0 01-3 3H9a3 3 0 01-3-3z" />
              </Box>
            )}
          </Box>
        </Stack>
      </Box>

      {/* Row 2: Category Tabs */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          p: '0.5rem 1.25rem',
          bgcolor: '#111318',
          borderBottom: '1px solid #1E2330',
          gap: '0.25rem',
          overflowX: 'auto',
        }}
      >
        {CATEGORY_TAB_ORDER.map((tab) => (
          <Box
            key={tab}
            component="button"
            onClick={() => handleCategoryChange(tab)}
            sx={{
              px: 1.5,
              py: 0.5,
              fontSize: '0.8rem',
              fontWeight: 600,
              background:
                categoryTab === tab
                  ? 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)'
                  : 'transparent',
              border: 'none',
              borderRadius: '6px',
              color: categoryTab === tab ? 'white' : '#6B7280',
              cursor: 'pointer',
              transition: 'all 0.15s ease',
              whiteSpace: 'nowrap',
              '&:hover': {
                color: categoryTab === tab ? 'white' : '#E8EAED',
              },
            }}
          >
            {CATEGORY_TAB_LABELS[tab]}
          </Box>
        ))}

        {/* Gear icon for custom column settings — shown when Custom is active */}
        {categoryTab === 'custom' && (
          <Box
            component="button"
            onClick={() => setShowColumnPicker(true)}
            title="Configure custom columns"
            sx={{
              width: 28,
              height: 28,
              borderRadius: '6px',
              border: '1px solid #1E2330',
              bgcolor: 'transparent',
              color: '#6B7280',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              ml: 0.5,
              transition: 'all 0.15s ease',
              '&:hover': { color: '#3B82F6', borderColor: '#3B82F6' },
            }}
          >
            <Box
              component="svg"
              viewBox="0 0 20 20"
              fill="currentColor"
              sx={{ width: 14, height: 14 }}
            >
              <path
                fillRule="evenodd"
                d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z"
                clipRule="evenodd"
              />
            </Box>
          </Box>
        )}
      </Box>

      {/* Table */}
      <Box sx={{ overflowX: 'auto', maxHeight: 600, overflowY: 'auto' }}>
        <Box
          component="table"
          sx={{ width: '100%', borderCollapse: 'collapse' }}
        >
          {/* Header */}
          <Box component="thead">
            <Box
              component="tr"
              sx={{ bgcolor: '#0D0F13', position: 'sticky', top: 0, zIndex: 10 }}
            >
              {/* Player Name Header */}
              <Box
                component="th"
                onClick={() => handleSort('player_name')}
                sx={{
                  p: '0.6rem 1rem',
                  textAlign: 'left',
                  fontSize: '0.85rem',
                  fontWeight: 800,
                  color: sort.key === 'player_name' ? '#3B82F6' : '#6B7280',
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em',
                  cursor: 'pointer',
                  borderBottom: '2px solid #1E2330',
                  position: 'sticky',
                  left: 0,
                  bgcolor: '#0D0F13',
                  minWidth: 180,
                  transition: 'color 0.15s ease',
                }}
              >
                <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                  Player
                  {sort.key === 'player_name' && (
                    <Box component="span" sx={{ color: '#3B82F6' }}>
                      {sort.direction === 'asc' ? '\u2191' : '\u2193'}
                    </Box>
                  )}
                </Box>
              </Box>

              {/* Stat Headers */}
              {currentColumns.map((col) => {
                const isActive = sort.key === col.key;
                return (
                  <Box
                    key={col.key}
                    component="th"
                    onClick={() => handleSort(col.key)}
                    sx={{
                      p: '0.6rem 0.5rem',
                      textAlign: 'center',
                      fontSize: '0.85rem',
                      fontWeight: 800,
                      color: isActive ? '#3B82F6' : '#6B7280',
                      textTransform: 'uppercase',
                      letterSpacing: '0.08em',
                      cursor: 'pointer',
                      borderBottom: '2px solid #1E2330',
                      minWidth: 56,
                      transition: 'color 0.15s ease',
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '0.25rem',
                      }}
                    >
                      {col.label}
                      {col.isPct && (
                        <Box component="span" sx={{ opacity: 0.5 }}>
                          %
                        </Box>
                      )}
                      {isActive && (
                        <Box component="span" sx={{ color: '#3B82F6' }}>
                          {sort.direction === 'asc' ? '\u2191' : '\u2193'}
                        </Box>
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>

          {/* Body */}
          <Box component="tbody">
            {sortedPlayers.map((player, index) => (
              <Box
                key={player.player_id || index}
                component="tr"
                onClick={() => setSelectedPlayerId(player.player_id)}
                sx={{
                  bgcolor: index % 2 === 0 ? '#0D0F13' : '#111318',
                  transition: 'background 0.15s ease',
                  '&:hover': { bgcolor: '#1A1D24' },
                  cursor: 'pointer',
                }}
              >
                {/* Player Name */}
                <Box
                  component="td"
                  sx={{
                    p: '0.5rem 1rem',
                    fontSize: '0.95rem',
                    fontWeight: 700,
                    color: '#E8EAED',
                    whiteSpace: 'nowrap',
                    borderBottom: '1px solid #1E2330',
                    position: 'sticky',
                    left: 0,
                    bgcolor: 'inherit',
                    borderLeft: '3px solid #3B82F6',
                  }}
                >
                  {player.player_name || '-'}
                </Box>

                {/* Stat Cells */}
                {currentColumns.map((col) => (
                  <StatCell
                    key={col.key}
                    player={player}
                    col={col}
                    rateMode={rateMode}
                    baselinePlayer={baselineMap.get(player.player_id)}
                    hasBaseline={hasBaseline}
                  />
                ))}
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {/* Footer */}
      <Box
        sx={{
          p: '0.5rem 1.25rem',
          background: 'linear-gradient(180deg, #111318 0%, #14171C 100%)',
          borderTop: '1px solid #1E2330',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography sx={{ fontSize: '0.75rem', color: '#6B7280' }}>
          {getFooterLeft()}
        </Typography>
        <Typography sx={{ fontSize: '0.75rem', color: '#6B7280' }}>
          {getFooterRight()}
        </Typography>
      </Box>

      {/* Empty state for custom mode with no columns selected */}
      {categoryTab === 'custom' && customColumns.length === 0 && (
        <Box
          sx={{
            p: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 1.5,
          }}
        >
          <Typography sx={{ fontSize: '0.9rem', color: '#6B7280' }}>
            No columns selected.
          </Typography>
          <Box
            component="button"
            onClick={() => setShowColumnPicker(true)}
            sx={{
              px: 2.5,
              py: 0.75,
              fontSize: '0.85rem',
              fontWeight: 600,
              color: 'white',
              background: 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              '&:hover': { opacity: 0.9 },
            }}
          >
            Choose Columns
          </Box>
        </Box>
      )}

      {/* Player Detail Card */}
      {selectedPlayerId != null && (() => {
        const selectedPlayer = filtered.players.find((p) => p.player_id === selectedPlayerId);
        const selectedBaseline = baselineMap.get(selectedPlayerId);
        if (!selectedPlayer || !selectedBaseline) return null;
        return (
          <NbaRatesPlayerDetailCard
            player={selectedPlayer}
            baselinePlayer={selectedBaseline}
            filteredAggregate={data.filtered.aggregate}
            baselineAggregate={data.baseline.aggregate}
            teamName={teamName}
            filterTerms={filterTerms}
            onClose={() => setSelectedPlayerId(null)}
          />
        );
      })()}

      {/* Custom Column Picker Modal */}
      {showColumnPicker && (
        <NbaRatesCustomColumnPicker
          selectedKeys={customColumnKeys}
          onChange={handleCustomColumnsChange}
          onClose={() => setShowColumnPicker(false)}
        />
      )}
    </Box>
  );
};

// ─── Inline: Stat Cell ────────────────────────────────────────────────────────

interface StatCellProps {
  player: NbaRatesPlayerStats;
  col: ColumnDef;
  rateMode: RateMode;
  baselinePlayer: NbaRatesPlayerStats | undefined;
  hasBaseline: boolean;
}

const StatCell: React.FC<StatCellProps> = ({
  player,
  col,
  rateMode,
  baselinePlayer,
  hasBaseline,
}) => {
  const cellValue = getCellValue(player, col.key, col, rateMode);
  const isTotals = rateMode === 'totals';
  const isRateStat = !!col.isPct;
  const isCountingStat = !!col.isCountingStat;
  const isAdjustedStat = !isTotals && isCountingStat;
  const isFptsPerMin = col.key === 'fpts_per_min' || col.key === 'fd_fpts_per_min';

  // ── Baseline logic ────────────────────────────────────────────────────
  // Show baseline for: rate/pct stats (always when baseline exists), counting stats in per100/per36 mode
  const showBaseline = hasBaseline && baselinePlayer != null && !isTotals &&
    (isRateStat || isAdjustedStat || isFptsPerMin);

  let baselineValue: number | null = null;
  if (showBaseline && baselinePlayer) {
    if (col.key === 'fpts_per_min') {
      baselineValue = calcFptsPerMin(baselinePlayer);
    } else if (col.key === 'fd_fpts_per_min') {
      baselineValue = calcFdFptsPerMin(baselinePlayer);
    } else if ((col.key === 'dk_fpts' || col.key === 'fd_fpts') && isCountingStat) {
      const baseFpts = col.key === 'dk_fpts' ? calcDkFpts(baselinePlayer) : calcFdFpts(baselinePlayer);
      if (rateMode === 'per100') {
        baselineValue = calcPer100(baseFpts, getPossessions(baselinePlayer, col));
      } else if (rateMode === 'per36') {
        baselineValue = calcPer36(baseFpts, baselinePlayer.minutes || 0);
      } else if (rateMode === 'per1') {
        baselineValue = calcPer1(baseFpts, baselinePlayer.minutes || 0);
      }
    } else if (isCountingStat) {
      const raw = baselinePlayer[col.key as keyof NbaRatesPlayerStats] as number | null;
      if (raw !== null) {
        if (rateMode === 'per100') {
          baselineValue = calcPer100(raw, getPossessions(baselinePlayer, col));
        } else if (rateMode === 'per36') {
          baselineValue = calcPer36(raw, baselinePlayer.minutes || 0);
        } else if (rateMode === 'per1') {
          baselineValue = calcPer1(raw, baselinePlayer.minutes || 0);
        }
      }
    } else if (isRateStat) {
      baselineValue = baselinePlayer[col.key as keyof NbaRatesPlayerStats] as number | null;
    }
  }

  // ── Relative delta for background shading ─────────────────────────────
  const relativeDelta = showBaseline
    ? calcRelativeDelta(cellValue, baselineValue)
    : null;

  const bgColor = (isTotals && !isRateStat) ? 'transparent' : getDeltaBackground(relativeDelta);

  // ── Baseline text color ───────────────────────────────────────────────
  let baselineTextColor = '#6B7280';
  if (relativeDelta !== null && Math.abs(relativeDelta) >= RELATIVE_THRESHOLD) {
    baselineTextColor = relativeDelta > 0 ? '#10B981' : '#EF4444';
  }

  // ── Tooltip ───────────────────────────────────────────────────────────
  let tooltipText = '';
  if (relativeDelta !== null && Math.abs(relativeDelta) >= RELATIVE_THRESHOLD) {
    const sign = relativeDelta > 0 ? '+' : '';
    tooltipText = `${sign}${relativeDelta.toFixed(0)}% vs season`;
  }

  // ── Format: use rate format when in per100/per36 for counting stats ───
  // For per1 mode, use formatDec2 for extra precision (values are small)
  const formatFn = rateMode === 'per1' && isCountingStat
    ? formatDec2
    : (!isTotals && isCountingStat && col.formatRate)
      ? col.formatRate
      : col.format;

  return (
    <Box
      component="td"
      title={tooltipText}
      sx={{
        p: showBaseline ? '0.35rem 0.5rem' : '0.5rem 0.5rem',
        textAlign: 'center',
        fontSize: '0.95rem',
        fontWeight: 500,
        color: '#E8EAED',
        whiteSpace: 'nowrap',
        borderBottom: '1px solid #1E2330',
        fontVariantNumeric: 'tabular-nums',
        bgcolor: bgColor,
        transition: 'background 0.15s ease',
      }}
    >
      <Box
        component="span"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          lineHeight: 1.1,
        }}
      >
        {/* Filtered value (main row) */}
        <Box component="span">
          {formatFn(cellValue)}
          {col.isPct && cellValue != null && (
            <Box component="span" sx={{ opacity: 0.5, ml: '1px' }}>
              %
            </Box>
          )}
        </Box>
        {/* Season baseline (second row) */}
        {showBaseline && baselineValue !== null && (
          <Box
            component="span"
            sx={{
              fontSize: '0.75rem',
              fontWeight: 500,
              color: baselineTextColor,
              mt: '2px',
            }}
          >
            {formatFn(baselineValue)}
            {col.isPct && (
              <Box component="span" sx={{ opacity: 0.5, ml: '1px' }}>
                %
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

// ─── Inline: Aggregate Stat ───────────────────────────────────────────────────

interface AggStatProps {
  label: string;
  value: string;
  valueColor?: string;
  bold?: boolean;
}

const AggStat: React.FC<AggStatProps> = ({ label, value, valueColor, bold }) => (
  <Stack direction="row" alignItems="baseline" spacing={0.5}>
    <Typography
      sx={{
        fontSize: '0.75rem',
        fontWeight: 600,
        color: '#6B7280',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
      }}
    >
      {label}
    </Typography>
    <Typography
      sx={{
        fontSize: '0.9rem',
        fontWeight: bold ? 700 : 600,
        color: valueColor || '#E8EAED',
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      {value}
    </Typography>
  </Stack>
);

// ─── Inline: Divider ──────────────────────────────────────────────────────────

const Divider: React.FC = () => (
  <Box sx={{ width: '1px', height: 24, bgcolor: '#2A2F3A' }} />
);

export default NbaRatesStatsTable;
